import StaffProfiles from '../../components/StaffProfiles';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import Calendar, { CalendarEvent } from '../../components/Calendar';
import EventStatusBadge from '../../components/EventStatusBadge';
import Modal from '../../components/Modal';
import PaymentQRCode from '../../components/PaymentQRCode';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import '../admin/EventCalendarPage.css';
import './MyCalendarPage.css';
import EventComments from '../../components/messaging/EventComments';
import EventAnnouncements from '../../components/messaging/EventAnnouncements';
import MemberEventRequirements from '../../components/MemberEventRequirements';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { translateError } from '../../utils/errorMessages';

// Extended event type with attendance info
interface MyCalendarEvent extends CalendarEvent {
  eventId: string;
  attending: boolean | null; // true=Yes, false=No, null=Maybe
  defaultAttendance: boolean | null;
  note?: string;
  price?: number;
  /** Available events only: what signing up would cost (never owed). */
  listPrice?: number;
  isPaid?: boolean;
  calendarSync: boolean;
  templateId?: number | null;
  predictedCoverage?: {
    method: 'calendar' | 'time' | 'entry' | 'needs_payment' | 'free';
    memberMembershipId: number | null;
    label?: { key: string; params?: Record<string, string | number> };
  };
}

const CALENDAR_BANNER_DISMISSED_KEY = 'calendarSyncBannerDismissed';
const STATUS_BANNER_DISMISSED_KEY = 'eventStatusBannerDismissed';
/** How far ahead the status banner looks. */
const STATUS_BANNER_DAYS = 14;

const MyCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [events, setEvents] = useState<MyCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calendar sync state
  const [hasCalendarToken, setHasCalendarToken] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(false);

  // Calendar sync banner
  const [showSyncBanner, setShowSyncBanner] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(CALENDAR_BANNER_DISMISSED_KEY);
    if (dismissed) return;

    api.calendar.getToken().then((res) => {
      if (!res.data.feedUrl) {
        setShowSyncBanner(true);
      }
    }).catch(() => {});
  }, []);

  const dismissBanner = () => {
    setShowSyncBanner(false);
    localStorage.setItem(CALENDAR_BANNER_DISMISSED_KEY, '1');
  };

  // Cancelled / moved sessions in the next two weeks. One row per event —
  // `events` is one entry per (event, member) pair.
  const statusAlerts = useMemo(() => {
    const now = Date.now();
    const horizon = now + STATUS_BANNER_DAYS * 24 * 60 * 60 * 1000;
    const seen = new Set<string>();
    return events
      .filter((event) => !event.available)
      .filter((event) => event.status === 'cancelled' || event.status === 'changed')
      .filter((event) => {
        const startsAt = new Date(event.startTime).getTime();
        return startsAt >= now && startsAt <= horizon;
      })
      .filter((event) => {
        if (seen.has(event.eventId)) return false;
        seen.add(event.eventId);
        return true;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [events]);

  // Dismissal is keyed on WHICH sessions are affected, not a flag: a banner
  // dismissed today must come back when a different session is cancelled.
  const alertSignature = statusAlerts.map((event) => `${event.eventId}:${event.status}`).join('|');
  const [dismissedSignature, setDismissedSignature] = useState<string>(
    () => localStorage.getItem(STATUS_BANNER_DISMISSED_KEY) || '',
  );
  const showStatusBanner = alertSignature !== '' && alertSignature !== dismissedSignature;

  const dismissStatusBanner = () => {
    setDismissedSignature(alertSignature);
    localStorage.setItem(STATUS_BANNER_DISMISSED_KEY, alertSignature);
  };

  // Calendar state
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<MyCalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<MyCalendarEvent | null>(null);
  const [updatingAttendance, setUpdatingAttendance] = useState(false);
  const [noteValue, setNoteValue] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'requirements' | 'comments'>('details');

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.my.events({ includeAvailable: 1 });
      const data = response.data as { events: MyCalendarEvent[]; hasCalendarToken: boolean };
      setEvents(data.events);
      setHasCalendarToken(data.hasCalendarToken);
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Deep-link: auto-open event modal when ?eventId= is in the URL
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    if (eventId && events.length > 0) {
      const match = events.find(e => e.eventId === eventId);
      if (match) {
        setSelectedEvent(match);
        setNoteValue(match.note || '');
        setSelectedDate(null);
        setSelectedDayEvents([]);
        setModalOpen(true);
      }
      // Clear the search param so it doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, events, setSearchParams]);

  // Calendar navigation
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  // Event click - show event details
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event as MyCalendarEvent);
    setNoteValue((event as MyCalendarEvent).note || '');
    setSelectedDate(null);
    setSelectedDayEvents([]);
    setModalOpen(true);
  };

  // Day click - show all events for that day
  const handleDayClick = (date: Date, dayEvents: CalendarEvent[]) => {
    setSelectedDate(date);
    setSelectedDayEvents(dayEvents as MyCalendarEvent[]);
    setSelectedEvent(null);
    setModalOpen(true);
  };

  // Update attendance for a member on an event
  const handleUpdateAttendance = async (event: MyCalendarEvent, newAttending: boolean | null) => {
    if (!event.member) return;

    setUpdatingAttendance(true);
    try {
      // Include note if it was edited
      const currentNote = noteValue !== (event.note || '') ? noteValue : undefined;
      await api.my.updateAttendance(event.eventId, event.member._id, newAttending, currentNote);

      const noteUpdate = currentNote !== undefined ? { note: currentNote } : {};

      setEvents((prevEvents) =>
        prevEvents.map((e) =>
          e._id === event._id ? { ...e, attending: newAttending, ...noteUpdate } : e
        )
      );
      if (selectedEvent && selectedEvent._id === event._id) {
        setSelectedEvent({ ...selectedEvent, attending: newAttending, ...noteUpdate });
      }
      setSelectedDayEvents((prevEvents) =>
        prevEvents.map((e) =>
          e._id === event._id ? { ...e, attending: newAttending, ...noteUpdate } : e
        )
      );
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setUpdatingAttendance(false);
    }
  };

  const handleSaveNote = async (event: MyCalendarEvent) => {
    if (!event.member || noteValue === (event.note || '')) return;

    setUpdatingAttendance(true);
    try {
      await api.my.updateAttendance(event.eventId, event.member._id, event.attending, noteValue);
      const noteUpdate = { note: noteValue };
      setEvents((prevEvents) =>
        prevEvents.map((e) => e._id === event._id ? { ...e, ...noteUpdate } : e)
      );
      if (selectedEvent && selectedEvent._id === event._id) {
        setSelectedEvent({ ...selectedEvent, ...noteUpdate });
      }
      setSelectedDayEvents((prevEvents) =>
        prevEvents.map((e) => e._id === event._id ? { ...e, ...noteUpdate } : e)
      );
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setUpdatingAttendance(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedDayEvents([]);
    setSelectedEvent(null);
  };

  // Calendar sync handlers
  const handleCalendarSyncToggle = (event: MyCalendarEvent) => {
    // If event has a template, show the recurring modal for both enable and disable
    if (event.templateId) {
      setSyncModalOpen(true);
      return;
    }
    // Non-recurring: just toggle the event
    syncEventOnly(event, !event.calendarSync);
  };

  const syncEventOnly = async (event: MyCalendarEvent, calendarSync: boolean) => {
    setSyncingCalendar(true);
    try {
      await api.calendar.syncEvent(Number(event.eventId), Number(event.member?._id), calendarSync);
      // Update local state
      setEvents(prev => prev.map(e =>
        e._id === event._id ? { ...e, calendarSync } : e
      ));
      if (selectedEvent?._id === event._id) {
        setSelectedEvent(prev => prev ? { ...prev, calendarSync } : prev);
      }
      setSelectedDayEvents(prev => prev.map(e =>
        e._id === event._id ? { ...e, calendarSync } : e
      ));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSyncingCalendar(false);
    }
  };

  const syncTemplateAll = async (event: MyCalendarEvent, calendarSync: boolean) => {
    setSyncingCalendar(true);
    try {
      await api.calendar.syncTemplate(Number(event.templateId), Number(event.member?._id), calendarSync);
      // Update all events from the same template for the same member
      setEvents(prev => prev.map(e =>
        (e.templateId === event.templateId && e.member?._id === event.member?._id)
          ? { ...e, calendarSync }
          : e
      ));
      if (selectedEvent?.templateId === event.templateId && selectedEvent?.member?._id === event.member?._id) {
        setSelectedEvent(prev => prev ? { ...prev, calendarSync } : prev);
      }
      setSelectedDayEvents(prev => prev.map(e =>
        (e.templateId === event.templateId && e.member?._id === event.member?._id)
          ? { ...e, calendarSync }
          : e
      ));
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSyncingCalendar(false);
      setSyncModalOpen(false);
    }
  };

  const formatDisplayDate = (date: Date): string => {
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    const formatted = date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    // Capitalize first letter (Czech returns lowercase)
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const formatEventTime = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    return `${start.toLocaleTimeString(locale, timeOptions)} - ${end.toLocaleTimeString(locale, timeOptions)}`;
  };

  const formatEventDateTime = (dateTime: string): string => {
    const date = new Date(dateTime);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleString(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getModalTitle = (): string => {
    if (selectedEvent) {
      return selectedEvent.name;
    }
    if (selectedDate) {
      return formatDisplayDate(selectedDate);
    }
    return '';
  };

  if (loading) {
    return <div className="calendar-page-loading">{t('calendar.loading')}</div>;
  }

  if (error) {
    return <p className="error-message">{t('common.error')}: {error}</p>;
  }

  return (
    <div className="event-calendar-page">
      <PageHeader title={t('calendar.title')} helpKey="help.user.calendar" />

      {showStatusBanner && (
        <div className="status-banner" role="status">
          <div className="status-banner__head">
            <strong>{t('eventStatus.bannerTitle', { count: statusAlerts.length })}</strong>
            <button
              type="button"
              className="status-banner__dismiss"
              onClick={dismissStatusBanner}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
          <ul className="status-banner__list">
            {statusAlerts.map((event) => (
              <li key={event.eventId}>
                <button
                  type="button"
                  className="status-banner__link"
                  onClick={() => setSearchParams({ eventId: event.eventId })}
                >
                  <EventStatusBadge status={event.status} compact />
                  <span className="status-banner__name">{event.name}</span>
                  <span className="status-banner__time">{formatEventDateTime(event.startTime)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showSyncBanner && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          borderRadius: '8px',
          backgroundColor: 'var(--info-bg, rgba(66, 133, 244, 0.1))',
          border: '1px solid var(--info-border, rgba(66, 133, 244, 0.3))',
        }}>
          <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            {t('calendarSync.banner')}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={() => navigate('/dashboard/preferences')}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#4285f4',
                color: 'white',
                fontSize: '0.85rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {t('calendarSync.bannerButton')}
            </button>
            <button
              onClick={dismissBanner}
              style={{
                padding: '0.4rem 0.5rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '1.1rem',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <Calendar
        year={currentYear}
        month={currentMonth}
        events={events}
        onDayClick={handleDayClick}
        onEventClick={handleEventClick}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
      />

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={getModalTitle()}
        size={selectedEvent ? "xlarge" : "small"}
      >
        {/* Day Events View */}
        {selectedDate && !selectedEvent && (
          <div className="modal-day-events">
            {selectedDayEvents.length === 0 ? (
              <p className="no-events-message">{t('calendar.noEventsDay')}</p>
            ) : (
              <div className="day-events-list">
                {selectedDayEvents.map((event) => (
                  <div
                    key={event._id}
                    className={`day-event-item ${event.available ? 'day-event-item--available' : event.attending === true ? 'attending' : event.attending === false ? 'not-attending' : 'maybe'}`}
                    style={event.member?.color ? { borderLeftColor: event.member.color } : undefined}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="day-event-info">
                      <div className="day-event-name">{event.name}</div>
                      {event.member && (
                        <div className="day-event-member" style={{ color: event.member.color }}>
                          {event.member.name}
                        </div>
                      )}
                    </div>
                    <div className="day-event-right">
                      {event.status && event.status !== 'scheduled' && (
                        <EventStatusBadge status={event.status} compact />
                      )}
                      {event.available ? (
                        <span className="attendance-badge available">{t('calendar.available')}</span>
                      ) : (
                        <span className={`attendance-badge ${event.attending === true ? 'yes' : event.attending === false ? 'no' : 'maybe'}`}>
                          {event.attending === true ? `${t('common.yes')} ✓` : event.attending === false ? `${t('common.no')} ✗` : `${t('common.maybe')} ?`}
                        </span>
                      )}
                      <div className="day-event-time">
                        {formatEventTime(event.startTime, event.endTime)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Event Details View - 2 column layout on desktop, tabs on mobile */}
        {selectedEvent && (
          <div className="modal-event-edit-layout">
            {/* Mobile Tab Switcher */}
            <div className="tab-switcher mobile-only">
              <button
                className={activeTab === 'details' ? 'active' : ''}
                onClick={() => setActiveTab('details')}
              >
                {t('eventDetails.tabDetails')}
              </button>
              <button
                className={activeTab === 'requirements' ? 'active' : ''}
                onClick={() => setActiveTab('requirements')}
              >
                {t('requirements.widgetTitle')}
              </button>
              <button
                className={activeTab === 'comments' ? 'active' : ''}
                onClick={() => setActiveTab('comments')}
              >
                {t('eventDetails.tabComments')}
              </button>
            </div>

            {/* Left column - Event details */}
            <div className={`modal-edit-form ${activeTab === 'details' ? 'active' : ''}`}>
              <div className="modal-event-details">
                <div className="event-detail-row">
                  <strong>{t('eventStatus.label')}</strong>
                  <p><EventStatusBadge status={selectedEvent.status} /></p>
                </div>
                {selectedEvent.member && (
                  <div className="event-detail-row">
                    <strong>{t('eventDetails.member')}</strong>
                    <p style={{ color: selectedEvent.member.color, fontWeight: 600 }}>
                      {selectedEvent.member.name}
                    </p>
                  </div>
                )}
                <div className="event-detail-row">
                  <strong>{t('eventDetails.description')}</strong>
                  {typeof selectedEvent.responsible === 'object' && selectedEvent.responsible?.name && <StaffProfiles people={[{ ...selectedEvent.responsible, name: selectedEvent.responsible.name }]} />}
                  <p style={{ whiteSpace: 'pre-line' }}>{selectedEvent.description || '-'}</p>
                </div>
                <div className="event-detail-row">
                  <strong>{t('eventDetails.startTime')}</strong>
                  <p>{formatEventDateTime(selectedEvent.startTime)}</p>
                </div>
                <div className="event-detail-row">
                  <strong>{t('eventDetails.endTime')}</strong>
                  <p>{formatEventDateTime(selectedEvent.endTime)}</p>
                </div>

                {selectedEvent.predictedCoverage && selectedEvent.predictedCoverage.method !== 'free' && selectedEvent.predictedCoverage.label && (
                  <div className="event-detail-row">
                    <strong>{t('events.coveredBy')}</strong>
                    <p style={{ color: 'var(--text-muted)' }}>
                      {t(selectedEvent.predictedCoverage.label.key, selectedEvent.predictedCoverage.label.params || {})}
                    </p>
                  </div>
                )}

                {selectedEvent.available && (
                  <div className="available-event-notice" role="status">
                    <span className="attendance-badge available">{t('calendar.available')}</span>
                    <p>{t('calendar.availableHint')}</p>
                    {(selectedEvent.listPrice ?? 0) > 0 && (
                      <p>
                        <strong>{t('events.price')}:</strong> {selectedEvent.listPrice} CZK
                      </p>
                    )}
                  </div>
                )}

                {!selectedEvent.available && (selectedEvent.price ?? 0) > 0 && selectedEvent.member && (
                  <div className="attendance-section" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <strong>{t('events.price')}</strong>
                        <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--orange-primary)' }}>
                          {selectedEvent.price} CZK
                        </p>
                        {selectedEvent.isPaid ? (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: 'var(--success-color, #38a169)',
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            marginTop: '0.5rem'
                          }}>
                            <span style={{ fontSize: '1.2rem' }}>✓</span>
                            {t('payments.paid')}
                          </div>
                        ) : (
                          <div style={{ marginTop: '1rem' }}>
                            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                              {t('publicEvent.paymentQRDesc')}
                            </p>
                            <div style={{ background: 'white', padding: '8px', borderRadius: '4px', display: 'inline-block' }}>
                              <PaymentQRCode
                                eventId={selectedEvent.eventId}
                                memberId={selectedEvent.member._id}
                                size={150}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedEvent.available ? null : selectedEvent.status === 'cancelled' ? (
                  // The backend refuses `attending: true` here, and saving a
                  // note re-sends the current value — so the whole control is
                  // locked rather than left to fail with a 409.
                  <div className="attendance-section">
                    <strong>{t('attendance.yourAttendance')}:</strong>
                    <p className="attendance-cancelled-notice" role="status">
                      <EventStatusBadge status="cancelled" />
                      <span>{t('attendance.cancelledLocked')}</span>
                    </p>
                  </div>
                ) : (
                <div className="attendance-section">
                  <strong>{t('attendance.yourAttendance')}:</strong>
                  <div className="attendance-dropdown-container">
                    <select
                      className={`attendance-dropdown ${selectedEvent.attending === true ? 'yes' : selectedEvent.attending === false ? 'no' : 'maybe'}`}
                      value={selectedEvent.attending === true ? 'yes' : selectedEvent.attending === false ? 'no' : 'null'}
                      onChange={(e) => {
                        const val = e.target.value;
                        const newAttending = val === 'yes' ? true : val === 'no' ? false : null;
                        handleUpdateAttendance(selectedEvent, newAttending);
                      }}
                      disabled={updatingAttendance}
                    >
                      <option value="yes">{t('common.yes')} ✓</option>
                      <option value="no">{t('common.no')} ✗</option>
                      <option value="null">{t('common.maybe')} ?</option>
                    </select>
                    {updatingAttendance && <span className="attendance-updating">{t('common.saving')}</span>}
                  </div>
                  <div className="attendance-note-wrapper">
                    <textarea
                      className="attendance-note-input"
                      placeholder={t('attendance.notePlaceholder')}
                      value={noteValue}
                      maxLength={500}
                      rows={2}
                      onChange={(e) => setNoteValue(e.target.value)}
                    />
                    {noteValue !== (selectedEvent.note || '') && (
                      <button
                        type="button"
                        className="attendance-note-save-btn"
                        onClick={() => handleSaveNote(selectedEvent)}
                        disabled={updatingAttendance}
                      >
                        {updatingAttendance ? t('common.saving') : t('common.save')}
                      </button>
                    )}
                  </div>
                </div>
                )}

                {hasCalendarToken && !selectedEvent.available && (
                  <div className="calendar-sync-section">
                    <Button
                      variant={selectedEvent.calendarSync ? 'secondary' : 'primary'}
                      size="sm"
                      onClick={() => handleCalendarSyncToggle(selectedEvent)}
                      loading={syncingCalendar}
                    >
                      {selectedEvent.calendarSync
                        ? t('calendarSync.removeFromCalendar')
                        : t('calendarSync.addToCalendar')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Right column - Widgets */}
            <div className={`modal-edit-widgets ${activeTab === 'requirements' || activeTab === 'comments' ? 'active' : ''}`}>
              <EventAnnouncements eventId={selectedEvent.eventId} />
              <CollapsibleWidget
                title={t('eventDetails.tabComments')}
                defaultExpanded={true}
              >
                <EventComments eventId={selectedEvent.eventId} />
              </CollapsibleWidget>

              {selectedEvent.member && !selectedEvent.available && (
                <CollapsibleWidget
                  title={t('requirements.widgetTitle')}
                  defaultExpanded={true}
                >
                  <MemberEventRequirements
                    eventId={selectedEvent.eventId}
                    memberId={selectedEvent.member._id}
                  />
                </CollapsibleWidget>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        title={t('calendarSync.syncOptions')}
        size="small"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
          <p style={{ margin: 0, color: 'var(--text-primary)' }}>
            {selectedEvent?.calendarSync
              ? t('calendarSync.removeRecurringQuestion')
              : t('calendarSync.addRecurringQuestion')}
          </p>
          <Button
            variant="secondary"
            onClick={() => {
              if (selectedEvent) {
                syncEventOnly(selectedEvent, !selectedEvent.calendarSync);
                setSyncModalOpen(false);
              }
            }}
            loading={syncingCalendar}
          >
            {t('calendarSync.justThisOne')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (selectedEvent) {
                syncTemplateAll(selectedEvent, !selectedEvent.calendarSync);
              }
            }}
            loading={syncingCalendar}
          >
            {t('calendarSync.allRecurring')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default MyCalendarPage;
